import { useRef, useState, useCallback } from 'react';
import { CloudUpload as CloudUploadIcon, Close as CloseIcon } from '@mui/icons-material';
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { useGetDriverDocumentQuery, useUploadDriverDocumentMutation } from '../services';
import type { DriverDocumentSide } from '../services';
import ImageLightbox from './ImageLightbox';

const SIDE_LABELS: Record<DriverDocumentSide, string> = {
    front: 'Frente',
    back: 'Dorso',
};
const ALL_SIDES: DriverDocumentSide[] = ['front', 'back'];
const FRONT_ONLY_TYPES = ['soa', 'matrícula'];
const HIDE_WHEN_EMPTY_TYPES = ['pasaporte'];

type DocumentSideItem = {
    side: DriverDocumentSide;
    imageUrl: string | null;
    isLoading: boolean;
    loadFailed: boolean;
};

type UploadModalState = {
    side: DriverDocumentSide;
    previewUrl: string | null;
    file: File | null;
    validTo: string;
    documentNumber: string;
};

function DocumentSideCard({
    imageUrl,
    isLoading,
    loadFailed,
    side,
    documentTypeName,
    editable,
    onPreview,
    onUploadClick,
}: DocumentSideItem & {
    documentTypeName: string;
    editable?: boolean;
    onPreview: (url: string, alt: string) => void;
    onUploadClick: (side: DriverDocumentSide) => void;
}) {
    const sideLabel = SIDE_LABELS[side];
    const alt = `${documentTypeName} — ${sideLabel}`;
    const placeholderText = isLoading ? 'Cargando...' : loadFailed ? 'Error al cargar' : 'Sin documento';

    return (
        <div className='flex flex-col gap-2 w-full min-w-0 min-h-[180px] rounded-lg overflow-hidden shadow-sm bg-white border border-gray-100'>
            {imageUrl ? (
                <div className='relative w-full flex-1 min-h-[160px] max-h-[360px]'>
                    <button
                        type='button'
                        onClick={() => onPreview(imageUrl, alt)}
                        className='w-full h-full min-h-[160px] max-h-[360px] rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2'
                        aria-label={`Ver ${alt}`}
                    >
                        <img
                            src={imageUrl}
                            alt={alt}
                            className='w-full h-full min-h-[160px] max-h-[360px] object-cover object-top'
                            referrerPolicy='no-referrer'
                        />
                    </button>
                    {editable && (
                        <button
                            type='button'
                            onClick={() => onUploadClick(side)}
                            className='absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 text-white text-xs font-medium shadow hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400'
                            aria-label={`Cambiar ${alt}`}
                        >
                            <CloudUploadIcon sx={{ fontSize: 14 }} />
                            Cambiar
                        </button>
                    )}
                </div>
            ) : (
                <div className='w-full flex-1 min-h-[160px] max-h-[360px] flex flex-col items-center justify-center gap-2 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg px-2 py-4'>
                    <span className='text-xs font-medium text-gray-500 uppercase'>{sideLabel}</span>
                    <span className='text-xs text-center text-gray-400'>{placeholderText}</span>
                    {editable && !isLoading && (
                        <button
                            type='button'
                            onClick={() => onUploadClick(side)}
                            className='mt-1 flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium shadow hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400'
                            aria-label={`Subir ${alt}`}
                        >
                            <CloudUploadIcon sx={{ fontSize: 14 }} />
                            Subir documento
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export type DriverDocumentTypeSectionProps = {
    driverId: string;
    documentTypeId: string;
    documentTypeName: string;
    editable?: boolean;
};

export default function DriverDocumentTypeSection({
    driverId,
    documentTypeId,
    documentTypeName,
    editable = false,
}: DriverDocumentTypeSectionProps) {
    const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);
    const [modal, setModal] = useState<UploadModalState | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showNotification } = useNotification();
    const [uploadDocument, { isLoading: isUploading }] = useUploadDriverDocumentMutation();

    const openLightbox = useCallback((url: string, alt: string) => setLightbox({ url, alt }), []);
    const closeLightbox = useCallback(() => setLightbox(null), []);

    const normalizedTypeName = documentTypeName.trim().toLowerCase();
    const isFrontOnly = FRONT_ONLY_TYPES.includes(normalizedTypeName);

    const { data: frontData, isError: frontError, isLoading: frontLoading } = useGetDriverDocumentQuery(
        { driverId, documentTypeId, side: 'front' },
        { skip: !driverId || !documentTypeId }
    );
    const { data: backData, isError: backError, isLoading: backLoading } = useGetDriverDocumentQuery(
        { driverId, documentTypeId, side: 'back' },
        { skip: !driverId || !documentTypeId || isFrontOnly }
    );

    const sidesToShow: DriverDocumentSide[] = isFrontOnly ? ['front'] : ALL_SIDES;
    const items: DocumentSideItem[] = sidesToShow.map((side) => ({
        side,
        imageUrl: side === 'front' ? (frontData?.imageUrl ?? null) : (backData?.imageUrl ?? null),
        isLoading: side === 'front' ? !!frontLoading : !!backLoading,
        loadFailed: side === 'front' ? !!frontError : !!backError,
    }));

    const hasAnyImage = items.some((t) => t.imageUrl != null);
    const hasAnyError = items.some((t) => t.loadFailed);
    const hasAnyLoading = items.some((t) => t.isLoading);
    const hide =
        (HIDE_WHEN_EMPTY_TYPES.includes(normalizedTypeName) && !hasAnyImage) ||
        (!editable && !hasAnyImage && !hasAnyError && !hasAnyLoading);
    if (hide) return null;

    const handleUploadClick = (side: DriverDocumentSide) => {
        setModal({ side, previewUrl: null, file: null, validTo: '', documentNumber: '' });
        setTimeout(() => fileInputRef.current?.click(), 0);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !modal) return;
        const previewUrl = URL.createObjectURL(file);
        setModal((prev) => prev ? { ...prev, file, previewUrl } : prev);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCloseModal = () => {
        if (modal?.previewUrl) URL.revokeObjectURL(modal.previewUrl);
        setModal(null);
    };

    const handleConfirmUpload = async () => {
        if (!modal?.file) return;

        const isFront = modal.side === 'front';
        if (isFront && !modal.validTo) {
            showNotification('La fecha de vencimiento es requerida para el frente del documento.', NotificationSeverity.error);
            return;
        }

        const formData = new FormData();
        formData.append('document', modal.file);
        formData.append('documentTypeId', documentTypeId);
        formData.append('side', modal.side);
        if (modal.validTo) formData.append('validTo', modal.validTo);
        if (modal.documentNumber.trim()) formData.append('documentNumber', modal.documentNumber.trim());

        try {
            await uploadDocument({ driverId, documentTypeId, side: modal.side, formData }).unwrap();
            showNotification('Documento actualizado correctamente', NotificationSeverity.success);
            handleCloseModal();
        } catch {
            showNotification('No se pudo subir el documento. Intentá de nuevo.', NotificationSeverity.error);
        }
    };

    const isFrontSide = modal?.side === 'front';

    return (
        <article>
            <h3 className='text-sm font-bold text-gray-800 uppercase mb-3'>{documentTypeName}</h3>
            <div className={`grid gap-4 w-full ${items.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {items.map((item) => (
                    <DocumentSideCard
                        key={item.side}
                        documentTypeName={documentTypeName}
                        editable={editable}
                        {...item}
                        onPreview={openLightbox}
                        onUploadClick={handleUploadClick}
                    />
                ))}
            </div>

            {/* Hidden file input */}
            {editable && (
                <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    className='hidden'
                    onChange={handleFileChange}
                />
            )}

            {/* Upload modal */}
            <Dialog open={!!modal} onClose={handleCloseModal} maxWidth='xs' fullWidth>
                <DialogTitle className='flex items-center justify-between pr-2'>
                    <span className='text-base font-semibold'>
                        {documentTypeName} — {modal ? SIDE_LABELS[modal.side] : ''}
                    </span>
                    <IconButton size='small' onClick={handleCloseModal} aria-label='Cerrar'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>
                <DialogContent className='flex flex-col gap-4 pt-2 pb-6'>
                    {modal?.previewUrl ? (
                        <img
                            src={modal.previewUrl}
                            alt='Vista previa'
                            className='w-full max-h-48 object-contain rounded-lg border border-gray-100 bg-gray-50'
                        />
                    ) : (
                        <button
                            type='button'
                            onClick={() => fileInputRef.current?.click()}
                            className='w-full h-32 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors text-sm font-medium'
                        >
                            <CloudUploadIcon />
                            Seleccionar imagen
                        </button>
                    )}

                    {modal?.previewUrl && (
                        <button
                            type='button'
                            onClick={() => fileInputRef.current?.click()}
                            className='text-xs text-blue-600 hover:underline self-start'
                        >
                            Cambiar imagen
                        </button>
                    )}

                    {isFrontSide && (
                        <div className='flex flex-col gap-1'>
                            <label className='text-xs font-semibold text-gray-600 uppercase tracking-wide'>
                                Fecha de vencimiento <span className='text-red-500'>*</span>
                            </label>
                            <input
                                type='date'
                                value={modal?.validTo ?? ''}
                                onChange={(e) => setModal((prev) => prev ? { ...prev, validTo: e.target.value } : prev)}
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
                            />
                        </div>
                    )}

                    <div className='flex flex-col gap-1'>
                        <label className='text-xs font-semibold text-gray-600 uppercase tracking-wide'>
                            Número de documento <span className='text-gray-400'>(opcional)</span>
                        </label>
                        <input
                            type='text'
                            value={modal?.documentNumber ?? ''}
                            onChange={(e) => setModal((prev) => prev ? { ...prev, documentNumber: e.target.value } : prev)}
                            placeholder='Ej: 12345678'
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
                        />
                    </div>

                    <div className='flex gap-2 justify-end mt-2'>
                        <button
                            type='button'
                            onClick={handleCloseModal}
                            disabled={isUploading}
                            className='px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                        >
                            Cancelar
                        </button>
                        <button
                            type='button'
                            onClick={handleConfirmUpload}
                            disabled={!modal?.file || (isFrontSide && !modal.validTo) || isUploading}
                            className='flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                        >
                            {isUploading && <CircularProgress size={14} sx={{ color: 'white' }} />}
                            {isUploading ? 'Subiendo...' : 'Guardar'}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {lightbox && (
                <ImageLightbox open onClose={closeLightbox} imageUrl={lightbox.url} alt={lightbox.alt} />
            )}
        </article>
    );
}
